import type { IPageInfo } from "pages/notice/store/NoticeVo";

export type TranslateTabType = "LIST" | "MANAGE";
export type translationLanguage = "KO" | "EN" | "JP";

/**
 * @interface ITranslateAdminListVo 번역 > 관리자(감수자) 일 경우 보여질 리스트
 * @property {number} translate_assign_job_id id값
 * @property {string} title 콘텐츠 제목
 * @property {string} language 언어
 * @property {string} content_provider_name cp사명
 * @property {string} distribution_channel_name 유통사명
 * @property {string} content_provider_name 채널명
 * @property {string} inspector_name 감수자(요청자)
 * @property {string} created_at 요청일자
 * @property {string} job_status 번역 상태
 * @property {string} worker_name 번역자(담당자)
 * @property {number} content_text_length 글자수
 * @property {string} grade 등급
 * @property {string} category_name 카테고리 이름
 * @property {string} duration_time 소요시간
 * @property {string} translate_grade 프리랜서 번역 등급
 
 */
export interface ITranslateAdminListVo {
  translate_assign_job_id: number;
  title: string;
  language?: string;
  content_provider_name: string;
  distribution_channel_name?: string;
  inspector_name?: string;
  created_at: string;
  job_status?: string;
  worker_name?: string;
  content_text_length?: number;
  grade: string;
  category_name: string;
  reserve_flag: boolean;

  duration_time?: number;
  translate_grade?: string;
}

export interface IResTranslateGridList {
  list: ITranslateAdminListVo[] | IFailueTranslationListVo[];
  page_info: IPageInfo;
}

/**
 * @interface ITranslationWorkerVo 번역 > 담당자 배정 팝업 > 작업자 리스트
 * @property {number} worker_user_id id
 * @property {string} worker_user_name 작업자 이름
 */
export interface ITranslationWorkerVo {
  worker_user_id: number;
  worker_user_name: string;
}

/**
 * @interface ITranslationInspectorVo 번역 > 요청자 배정 팝업 > 감수자 리스트
 * @property {number} inspector_user_id id
 * @property {string} inspector_user_name 감수자 이름
 */
export interface ITranslationInspectorVo {
  inspector_user_id: number;
  inspector_user_name: string;
}

/**
 * @interface ITranslateEditVo 번역 > 담당자 배정 팝업 > 작업자 리스트
 * @property {number} distribution_channel_category_id 채널 카테고리 id
 * @property {number} distribution_channel_id 채널id
 * @property {string} origin_contents_title 원본 타이틀
 * @property {string} origin_contents 원본(본문)
 * @property {string} origin_link 원본 컨텐츠 링크
 * @property {string} origin_contents_published_date 원본 발행일자
 * @property {string} system_contents_title 자동번역본 타이틀
 * @property {string} system_contents 자동번역본 본문
 * @property {string} system_created_at 자동번역 일자
 * @property {string} worker_title 프리랜서(작업자) 타이틀
 * @property {string} worker_contents 프리랜서(작업자) 작업본
 * @property {string} inspector_title 감수자 타이틀
 * @property {string} inspector_contents 감수자 본문 (최종)
 * @property {ITranslateEditMessageVo} messages 메모
 * @property {string} language 언어
 * @property {string} translate_grade 프리랜서 번역 등급
 */
export interface ITranslateEditVo {
  distribution_channel_category_id: number;
  distribution_channel_id: number;

  origin_contents_title: string;
  origin_contents: string;
  origin_link: string;
  origin_contents_published_date: string;
  system_contents_title: string;
  system_contents: string;
  system_created_at: string;
  worker_title: string;
  worker_contents: string;
  inspector_title: string;
  inspector_contents: string;

  messages?: ITranslateEditMessageVo[];
  language: string;
  translate_grade?: string;
}

/**
 * @interface ITranslateEditMessageVo 번역 > 상세내역 > message 정보
 * @property {string} user_type 유저 타입 / 감수자, 번역자
 * @property {number} user_id id
 * @property {string} user_name 이름
 * @property {string} message 메모 이름
 *
 */
export interface ITranslateEditMessageVo {
  user_type: string;
  user_id: number;
  user_name: string;
  message: string;
}

/**
 * @enum TranslateEditStatus 에디터 상태값(상태에 따라 버튼 달라짐)
 * @type CHECK 원본 / 자동번역본
 * @type EDIT : 애디터 활성
 * @type SAVE : 애디터 저장
 * @type DIFF : 원본 / DIFF
 * @type RESULT : 프리랜서 작업본 / 배포 완료본
 */
export enum TranslateEditStatus {
  CHECK = "CHECK",
  EDIT = "EDIT",
  SAVE = "SAVE",
  DIFF = "DIFF",
  READY = "READY",
  INSPECT = "INSPECT",
  RESULT = "RESULT",
  REJECT = "REJECT",
  DISTRIBUTION_READY = "DISTRIBUTION_READY",
  DISTRIBUTION = "DISTRIBUTION",
}

/**
 * @interface IResDistributionChannelListVo 번역 배포 채널 리스트
 * @property {number} distribution_channel_id 채널id
 * @property {string} distribution_channel_name 채널 이름
 * @property {string} cms_type 워드프레스/cms 타입
 * @property {IContentPPChannelLisCategoryVo} category_list 카테고리 리스트
 */
export interface IResDistributionChannelListVo {
  distribution_channel_projection: IDistributionListVo[];
  origin_distribution_channel: {
    origin_distribution_channel_id: number;
    origin_distribution_channel_name: string;
    cms_type: string;
    origin_distribution_channel_category_id: number;
    origin_distribution_channel_category_name: string;
  };
}

export interface IDistributionListVo {
  distribution_channel_id: number;
  distribution_channel_name: string;
  cms_type: string;
  category_list: IContentPPChannelLisCategoryVo[];
}
/**
 * @interface IContentPPChannelLisCategoryVo 콘텐츠 pp 카테고리 리스트
 * @property {number} distribution_channel_id 채널 id
 * @property {string} distribution_channel_name 채널 이름
 * @property {number} distribution_channel_category_id 카테고리 id
 * @property {string} distribution_channel_category_name 카테고리 이름
 */
export interface IContentPPChannelLisCategoryVo {
  distribution_channel_id: number;
  distribution_channel_name: string;
  distribution_cms_type: string;
  distribution_channel_category_id: number;
  distribution_channel_category_name: string;
}

/**
 * 검색 조건 저장 쿠키 관련 데이터 형식
 */
export interface ISearchCookieData {
  tab: TranslateTabType;
  page: number;
  start_date: string;
  end_date: string;
  date_number: string | number;
  language?: string;
  job_status?: string;
  search_type?: string;
  search_text?: string;
  inspector_id?: string;
  reserve_flag?: string;
}

/**
 * @interface ITranslationTitleSelectVo 번역 > 감수 edit > 타이틀 선택
 * @property {number} translate_title_id id
 * @property {string} translate_title 타이틀
 */
export interface ITranslationTitleSelectVo {
  translate_title_id: number;
  translate_title: string;
}

/**
 * 책갈피 저장용 컨텐츠 정보
 */
export interface IContentBookmarkDo {
  contentsId: number;
  title: string;
  expired: number;
}

/**
 * @interface IFailueTranslationList 번역 실패 리스트
 */
export interface IFailueTranslationListVo {
  translator_job_request_id: number;
  process_id: number;
  contents_id: 0;
  distribution_channel_id: number;
  distribution_channel_category_id: number;
  distribution_channel_name: string;
  distribution_channel_category_name: string;
  translate_status: TranslateStatus;
  fail_retry_cnt: number;
  translate_language: translationLanguage;
  thumbnail_path: string;
  cp_name: string;
  category_names: string;
  content_type: string;
  published_date: string;
  title: string;
  link: string;
  author: string;
  content_text_length: number;
  created_at: string;
  updated_at: string;
}

/**
 * 번역 상태
 * @interface TranslateStatus 번역 상태
 * @description 번역의 현재 상태를 나타냅니다.
 * NONE: 대상 아님
 * READY: 번역 대기중
 * PROCESSING: 번역중
 * SUCCESS: 번역 성공
 * FAIL: 번역 실패
 */
export enum TranslateStatus {
  none = "NONE",
  ready = "READY",
  processing = "PROCESSING",
  success = "SUCCESS",
  fail = "FAIL",
}

/**
 * @interface IFailueTranslationDetail 번역 실패 상세
 */
export interface IFailueTranslationDetailVo {
  translate_job_request_id: number;
  translator_id: number;
  contents_id: number;

  //원문
  title: string;
  description: string;
  auth: string;
  tags: string[];
  contents: string;

  translate_language: translationLanguage;

  //번역
  translate_title_id: number;
  translate_title: string;
  translate_title_module: string;
  translate_title_module_version?: string;
  translate_title_prompt_id: number;

  translate_info_id: number;
  translate_info_module: string;
  translate_info_module_version?: string;

  translate_contents_id: number;
  translate_contents: string;
  translate_contents_module: string;
  translate_contents_module_version?: string;
  translate_contents_prompt_id: number;

  translate_description: string;
  translate_auth: string;
  translate_tags: string[];
}

export interface ITranslationPromptVo {
  id: number;
  translate_language: translationLanguage;
  translate_type: "TITLE" | "CONTENTS" | "INFO";
  type: string;
  count: number;
  category_group: string;
  prompt: string;
  created_at: string;
  updated_at: string;
}
